import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { Link, useParams } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────
// Hôte des applications partagées par la marketplace.
//
// Quand un vendeur ouvre une app depuis son tableau de bord
// (/vendor-dashboard → Applications), elle s'ouvre ICI, dans son
// propre compte, et opère sur SES données (les pages utilisent
// l'utilisateur connecté). On ne renvoie donc plus le vendeur vers
// /dashboard/<app> de la marketplace.
// ─────────────────────────────────────────────────────────────

type LazyPage = LazyExoticComponent<ComponentType<Record<string, unknown>>>;

const APPS: Record<string, LazyPage> = {
  zcalendar: lazy(() => import('@/pages/dashboard/apps/zcalendar/page')),
  forms: lazy(() => import('@/pages/dashboard/apps/forms/page')),
  gestionpro: lazy(() => import('@/pages/dashboard/apps/gestionpro/page')),
  zcall: lazy(() => import('@/pages/dashboard/apps/zcall/page')),
  'file-manager': lazy(() => import('@/pages/dashboard/apps/file-manager/page')),
  documents: lazy(() => import('@/pages/dashboard/apps/documents/page')),
  'mon-assistant': lazy(() => import('@/pages/dashboard/apps/mon-assistant/page')),
  'customers-manager': lazy(() => import('@/pages/dashboard/apps/customers-manager/page')),
  'ma-fidelite': lazy(() => import('@/pages/dashboard/apps/ma-fidelite/page')),
  'fidelite-recompenses': lazy(() => import('@/pages/dashboard/apps/fidelite-recompenses/page')),
  hebergement: lazy(() => import('@/pages/dashboard/apps/hebergement/page')),
  formation: lazy(() => import('@/pages/dashboard/apps/formation/page')),
  credit: lazy(() => import('@/pages/dashboard/credit/page')),
  caisse: lazy(() => import('@/pages/dashboard/caisse/page')),
  'seo-optimizer': lazy(() => import('@/pages/dashboard/apps/seo-optimizer/page')),
  'chat-live': lazy(() => import('@/pages/dashboard/apps/chat-live/page')),
  zifekbi: lazy(() => import('@/pages/dashboard/apps/zifekbi/page')),
  'facebook-pixel': lazy(() => import('@/pages/dashboard/apps/facebook-pixel/page')),
  'ia-reporting': lazy(() => import('@/pages/dashboard/apps/ia-reporting/page')),
  'analytics-advanced': lazy(() => import('@/pages/dashboard/apps/analytics-advanced/page')),
  'analytics-pro': lazy(() => import('@/pages/dashboard/apps/analytics-pro/page')),
  'customer-support': lazy(() => import('@/pages/dashboard/apps/customer-support/page')),
  'whatsapp-button': lazy(() => import('@/pages/dashboard/apps/whatsapp-button/page')),
  'reseaux-sociaux': lazy(() => import('@/pages/dashboard/apps/reseaux-sociaux/page')),
  'email-marketing': lazy(() => import('@/pages/dashboard/apps/email-marketing/page')),
  'gestion-stock': lazy(() => import('@/pages/dashboard/apps/gestion-stock/page')),
  'sauvegarde-auto': lazy(() => import('@/pages/dashboard/apps/sauvegarde-auto/page')),
  paypal: lazy(() => import('@/pages/dashboard/apps/paypal/page')),
  'content-studio': lazy(() => import('@/pages/dashboard/apps/content-studio/page')),
  zstudio: lazy(() => import('@/pages/dashboard/apps/zstudio/page')),
};

const APP_LABELS: Record<string, string> = {
  zcalendar: 'ZCalendar',
  forms: 'Formulaires',
  gestionpro: 'GestionPro',
  zcall: 'ZCall',
  'file-manager': 'Fichiers',
  documents: 'Documents',
  'mon-assistant': 'Mon Assistant',
  'customers-manager': 'Gestion clients',
  'ma-fidelite': 'Ma Fidélité',
  'fidelite-recompenses': 'Fidélité & Récompenses',
  hebergement: 'Hébergement',
  formation: 'Formation',
  credit: 'Zifek Crédit',
  caisse: 'Ma Caisse',
  'seo-optimizer': 'SEO Optimizer',
  'chat-live': 'Chat Live',
  zifekbi: 'Zifek BI',
  'facebook-pixel': 'Facebook Pixel',
  'ia-reporting': 'Analyse & Reporting IA',
  'analytics-advanced': 'Analytics Avancés',
  'analytics-pro': 'Analytics Pro',
  'customer-support': 'Service Client',
  'whatsapp-button': 'Bouton WhatsApp',
  'reseaux-sociaux': 'Réseaux sociaux',
  'email-marketing': 'Email Marketing',
  'gestion-stock': 'Gestion du stock',
  'sauvegarde-auto': 'Sauvegarde Auto',
  paypal: 'PayPal',
  'content-studio': 'Content Studio',
  zstudio: 'ZStudio',
};

export default function VendorAppHost() {
  const { slug } = useParams<{ slug: string }>();
  const key = (slug || '').toLowerCase();
  const App = APPS[key];
  const label = APP_LABELS[key] || key;

  return (
    <div className="min-h-screen bg-background-50">
      <section className="border-b border-background-200/70 bg-background-100/40">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-4 md:py-5 flex items-center gap-3">
          <Link
            to="/vendor-dashboard"
            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-background-50 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors no-underline whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Tableau de bord
          </Link>
          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-bold font-heading text-foreground-950 truncate">{label}</h1>
            <p className="text-[11px] text-foreground-500">Application dans votre compte vendeur</p>
          </div>
        </div>
      </section>

      <div className="w-full max-w-7xl mx-auto">
        {App ? (
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-24">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              </div>
            }
          >
            <App />
          </Suspense>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center px-4">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-apps-2-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-base font-semibold text-foreground-900 mb-1">Application indisponible</h2>
            <p className="text-sm text-foreground-500 max-w-sm mb-5">
              Cette application n&apos;est pas encore disponible dans votre espace vendeur.
            </p>
            <Link
              to="/vendor-dashboard"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Retour au tableau de bord
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}